import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { CompleteOnboardingMutation, resetMockOnboarding, UserQuery } from '~/modules/user'
import { authenticate, hasSession, setReauthenticate, signOut } from '~/shared/api'
import { LOCAL_STORAGE_KEYS, USE_MOCKS } from '~/shared/config'
import { createLocalStorageHook } from '~/shared/lib/persist'
import { usePlatform } from '~/shared/platform'

import { SessionContext } from './context'
import { resolveSessionStatus } from './resolveStatus'
import type { SessionContextValue } from './types'

type SessionProviderProps = {
  children: ReactNode
}

/**
 * Оптимистичный кэш признака «онбординг пройден». Источник правды — сервер (`onboardedAt`
 * в `GET /users/me`), здесь лежит только последний известный ответ.
 *
 * Зачем кэш вообще: `/users/me` отвечает не мгновенно, и без него каждый повторный запуск
 * начинался бы со сплэша. Кэш работает в одну сторону — «да, проходила»: он может показать
 * приложение раньше ответа сервера, но не может отправить в онбординг того, кого сервер
 * считает зарегистрированным. Поэтому пустой кэш (чистка браузера, другое устройство) —
 * это ожидание ответа, а не показ регистрации заново.
 */
const useOnboardedCache = createLocalStorageHook(LOCAL_STORAGE_KEYS.onboardingSeen, {
  type: 'boolean',
  defaultValue: false,
})

export function SessionProvider(props: SessionProviderProps) {
  const { children } = props
  const platform = usePlatform()
  const [onboardedCache, setOnboardedCache] = useOnboardedCache()

  const [isResolved, setIsResolved] = useState(false)
  const [hasAuthError, setHasAuthError] = useState(false)
  // Онбординг завершён в этом запуске приложения. Живёт в React-состоянии, а не в кэше запроса,
  // потому что должен переключить статус синхронно — в том же кадре, в котором страница
  // онбординга уводит на главную (см. `completeOnboarding`).
  const [hasJustCompleted, setHasJustCompleted] = useState(false)
  const hasStartedRef = useRef(false)

  // Протух и refresh-токен — входим заново тем же способом, что и на старте. Без этого
  // приложение после суток простоя рисовало пустые экраны: refresh не проходил, каждый
  // запрос отдавал 401, а перелогиниться было некому.
  useEffect(() => {
    if (USE_MOCKS) {
      return
    }

    setReauthenticate(async () => {
      try {
        await authenticate({ initData: platform.initData })
        return true
      } catch {
        return false
      }
    })

    return () => setReauthenticate(null)
  }, [platform.initData])

  useEffect(() => {
    // StrictMode монтирует эффекты дважды — вход должен уйти ровно один раз.
    if (hasStartedRef.current) {
      return
    }
    hasStartedRef.current = true

    const run = async () => {
      try {
        // Сессия от прошлого запуска — повторно логиниться незачем: протухший access
        // обновит сам httpClient по refresh-токену.
        if (!hasSession()) {
          await authenticate({ initData: platform.initData })
        }
      } catch {
        // Не смогли войти — приложение всё равно должно отрисоваться и показать ошибку,
        // а не остаться в вечном скелетоне.
        setHasAuthError(true)
      } finally {
        setIsResolved(true)
      }
    }

    void run()
  }, [platform.initData])

  const isAuthenticated = isResolved && !hasAuthError && Boolean(platform.user)

  // Профиль спрашиваем сразу после входа: `onboardedAt` в нём — единственный источник правды
  // о том, дошёл ли человек до конца регистрации.
  const meQuery = UserQuery.useQuery(undefined, { enabled: isAuthenticated })
  const { invalidate: invalidateMe, setData: setMeData } = UserQuery.useCacheController()
  const [completeOnboardingRequest] = CompleteOnboardingMutation.useMutation()

  const serverOnboarded = meQuery.data ? meQuery.data.onboardedAt !== null : null

  // Ответ сервера подменяет кэш — в том числе в обратную сторону: аккаунт удалили и завели
  // заново, а флаг в браузере остался с прошлой жизни. Кроме одного случая: онбординг только
  // что закончился здесь и сейчас — тогда «нет» от сервера означает лишь то, что ответ ехал
  // дольше, чем отметка о завершении.
  useEffect(() => {
    if (hasJustCompleted) {
      return
    }
    if (serverOnboarded !== null && serverOnboarded !== onboardedCache) {
      setOnboardedCache(serverOnboarded)
    }
  }, [hasJustCompleted, serverOnboarded, onboardedCache, setOnboardedCache])

  const status = useMemo(
    () =>
      resolveSessionStatus({
        isResolved,
        hasAuthError,
        hasUser: Boolean(platform.user),
        serverOnboarded,
        // `null` — значения в LocalStorage нет вовсе, это то же самое, что «не проходила».
        onboardedCache: Boolean(onboardedCache),
        hasJustCompleted,
        isMeError: meQuery.isError,
      }),
    [
      isResolved,
      hasAuthError,
      platform.user,
      serverOnboarded,
      onboardedCache,
      hasJustCompleted,
      meQuery.isError,
    ],
  )

  const completeOnboarding = useCallback(async () => {
    // Всё, что переключает статус, делается синхронно, ДО запроса: страница онбординга уводит
    // на главную в том же обработчике, и React сводит оба обновления в один кадр. Иначе роутер
    // успевает увидеть «онбординг не пройден» на главной и вернуть человека на первый шаг —
    // тот самый мелькающий экран (правка основателя №1).
    setHasJustCompleted(true)
    // Кэш ставим до запроса: даже если сеть подведёт, человек уже прошёл регистрацию и
    // видеть её снова не должен. Сервер догонит при следующем удачном вызове.
    setOnboardedCache(true)
    // Профиль в кэше тоже помечаем пройденным — иначе экраны, которые читают `onboardedAt`,
    // до ответа сервера считали бы регистрацию незаконченной. Настоящий профиль приедет
    // ответом мутации (см. её sideEffects). Пустой кэш не трогаем: подсовывать выдуманный
    // профиль вместо отсутствующего нельзя.
    setMeData(undefined, (previous) =>
      previous ? { ...previous, onboardedAt: new Date() } : previous,
    )

    await completeOnboardingRequest()
  }, [completeOnboardingRequest, setMeData, setOnboardedCache])

  const reset = useCallback(() => {
    setHasJustCompleted(false)
    setOnboardedCache(false)
    if (USE_MOCKS) {
      // Без бэка сбрасываем «серверную» отметку в моках, иначе GET /users/me тут же вернёт
      // пройденный онбординг и кнопка дев-панели ничего не изменит.
      resetMockOnboarding()
      void invalidateMe()
      return
    }
    signOut()
  }, [invalidateMe, setOnboardedCache])

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      user: platform.user,
      completeOnboarding,
      reset,
    }),
    [status, platform.user, completeOnboarding, reset],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext)
  if (!value) {
    throw new Error('useSession должен вызываться внутри <SessionProvider>')
  }
  return value
}
